import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import TextWinds from '../../../common/TextWinds';
import {
  AppProductListOrderBy,
  ProductSubType,
  ProductType,
} from '../../../../enum';
import { BoostUpTypes, LmsInfoTypes } from '../../../../constants/types';
import BoostupWidget from './BoostupWidget';
import instance from '../../../../api/axios/utils.ts/instance';
import {
  BookOpenIcon,
  PlayCircleIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline';
import { useAppDispatch } from '../../../../store/hook';
import { openAlert } from '../../../../store/features/modalSlice';
import { getErrorMessages } from '../../../../api/utils/util-func';

export type Filter = 'ALL' | 'COURSE' | 'BOOSTUP';

const filterOptions: {
  label: string;
  value: Filter;
  icon: React.ElementType;
}[] = [
  { label: '전체', value: 'ALL', icon: Squares2X2Icon },
  { label: '코스', value: 'COURSE', icon: PlayCircleIcon },
  { label: '교재/교구', value: 'BOOSTUP', icon: BookOpenIcon },
];

const BoostUp = ({
  lmsLists,
  classId,
}: {
  lmsLists: LmsInfoTypes;
  classId: string;
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialFilter = searchParams.get('filter');
  const [filter, setFilter] = useState<Filter>(
    initialFilter === 'COURSE' || initialFilter === 'BOOSTUP'
      ? initialFilter
      : 'ALL',
  );
  const [boostupLists, setBoostupLists] = useState<BoostUpTypes[]>([]);

  const handleFilterChange = (value: Filter) => {
    setFilter(value);
    setSearchParams(
      (prev) => {
        if (value === 'ALL') {
          prev.delete('filter');
        } else {
          prev.set('filter', value);
        }
        return prev;
      },
      { replace: true },
    );
  };

  const dispatch = useAppDispatch();

  const params = {
    product_type: ProductType.BOOSTUP,
    product_sub_type: ProductSubType.PRODUCT,
    order_by: AppProductListOrderBy.RECOMMENDED,
    courseId: lmsLists.course?.id,
  };

  useEffect(() => {
    if (lmsLists.course) {
      const fetchBoostupLists = async () => {
        try {
          await instance.get('/v1/product', { params }).then((response) => {
            const sortedList = [...response.data.result].sort(
              (a, b) =>
                Number(b.product_type === 'COURSE') -
                Number(a.product_type === 'COURSE'),
            );

            setBoostupLists(sortedList);
          });
        } catch (error) {
          dispatch(openAlert({ message: getErrorMessages(error) }));
        }
      };

      fetchBoostupLists();
    }
  }, [lmsLists.course]);

  const filteredBoostupLists = (boostupLists ?? []).filter((boostup) => {
    if (!filter || filter === 'ALL') return true;

    return boostup.product_type === filter;
  });

  const isLoading = (): boolean => {
    if (!boostupLists) {
      return true;
    } else {
      return false;
    }
  };

  // 부스트업 리스트가 없거나 길이가 0이면 컴포넌트를 렌더링하지 않음
  if (isLoading()) {
    return null;
  }

  return (
    <div className="rounded-lg bg-white px-3 pt-6 shadow-card xl:px-8">
      <div className="mb-4 flex items-center gap-2">
        {filterOptions.map((option) => {
          const isActive = filter === option.value;
          const Icon = option.icon;

          return (
            <button
              key={option.value}
              type="button"
              className={[
                'flex items-center gap-1.5 rounded-full border px-3 py-1.5 transition-colors',
                isActive
                  ? 'border-purple5 bg-purple5'
                  : 'border-gray4 bg-white hover:bg-gray1',
              ].join(' ')}
              onClick={() => handleFilterChange(option.value)}
            >
              <Icon
                className={[
                  'h-4 w-4',
                  isActive ? 'text-white' : 'text-gray8',
                ].join(' ')}
              />

              <TextWinds type="caption1" color={isActive ? 'white' : 'gray8'}>
                {option.label}
              </TextWinds>
            </button>
          );
        })}
      </div>

      <div className="grid h-auto grid-cols-3 gap-x-[15px] gap-y-[23px] pb-8 xl:grid-cols-6 xl:gap-x-6 xl:gap-y-6">
        <BoostupWidget classId={classId} datas={filteredBoostupLists} />
      </div>
    </div>
  );
};

export default BoostUp;
